import React from 'react';
import { BarChart3, Check, Cpu, Headphones, LifeBuoy, Megaphone, MessageSquare, Receipt, Sparkles, TrendingUp, Users } from 'lucide-react';
import { IntegrationLogo } from '../IntegrationLogo';
import { PixelCursor } from '../alt/PixelCursor';

/**
 * THE ARTEFACTS ON THE AI AGENTS PAGE ARE NOT SCREENSHOTS.
 *
 * They are idealised product objects: the same components the real UI uses,
 * but with larger type, fewer elements and no chrome, so each one is legible
 * at a third of a screen and reads as ONE designed thing rather than a photo
 * of a window. A literal screenshot shrunk to this size loses its own labels,
 * which is the failure mode this file exists to avoid.
 *
 * They all share the site's surface language: white fill, the warm #E7E0D4
 * hairline, a 22px radius on the outer objects, and the two-part shadow used
 * by every card on the home page.
 */

export const CARD_SHADOW = 'shadow-[0_1px_2px_rgba(43,33,26,0.05),0_18px_40px_-18px_rgba(43,33,26,0.22)]';

/**
 * Category chips. Colour is FUNCTIONAL here, not decorative — it is how an
 * operator scans a directory of agents for the ones that belong to their
 * department, so this is the one place on the site where a second and third
 * hue are earned.
 *
 * They were originally held at very low saturation, which defeated the point:
 * six chips within a few percent of each other in both hue and chroma cannot
 * be told apart at a glance, so they stopped being a scanning aid and just
 * added grey noise to every card. Each ground is now a clear, distinct tint
 * and each label is a saturated version of the same hue, held dark enough to
 * clear 4.5:1 on its own ground. Sales stays the brand orange, since sales is
 * the department the page's own examples lead with.
 */
const CATEGORIES = {
  sales: {
    label: 'Sales',
    icon: TrendingUp,
    className: 'bg-[#FFE6D2] text-[#B03E06]'
  },
  marketing: {
    label: 'Marketing',
    icon: Megaphone,
    className: 'bg-[#D6F2E0] text-[#0F6B45]'
  },
  support: {
    label: 'Support',
    icon: LifeBuoy,
    className: 'bg-[#DAEAFC] text-[#14549C]'
  },
  ops: {
    label: 'Operations',
    icon: BarChart3,
    className: 'bg-[#FCEAC4] text-[#8A5A05]'
  },
  people: {
    label: 'People',
    icon: Users,
    className: 'bg-[#E8DFFB] text-[#5732B8]'
  },
  finance: {
    label: 'Finance',
    icon: Receipt,
    className: 'bg-[#D2F0EC] text-[#0B6961]'
  }
} as const;
export type AgentCategory = keyof typeof CATEGORIES;
export type AgentTemplate = {
  category: AgentCategory;
  name: string;
  blurb: string;
  /** Catalog slugs — resolved through the shared logo chain. */
  tools: [string, string][];
};

/**
 * Real recurring responsibilities rather than demo filler: each is a job a
 * person currently does by hand on a schedule, which is the whole argument for
 * giving it an owner.
 */
export const AGENT_TEMPLATES: AgentTemplate[] = [{
  category: 'sales',
  name: 'Renewal brief agent',
  blurb: 'Builds a brief before every renewal call from the CRM, the email thread and past notes.',
  tools: [['salesforce', 'Salesforce'], ['gmail', 'Gmail']]
}, {
  category: 'marketing',
  name: 'Campaign recap agent',
  blurb: 'Posts weekly performance with the top movers and what changed since last week.',
  tools: [['hubspot', 'HubSpot'], ['googleanalytics', 'Analytics']]
}, {
  category: 'support',
  name: 'Escalation watcher',
  blurb: 'Flags conversations going quiet and files the ticket with the full context attached.',
  tools: [['intercom', 'Intercom'], ['linear', 'Linear']]
}, {
  category: 'ops',
  name: 'Monday recap agent',
  blurb: "Reads the week's meetings and posts what moved, what slipped and who is waiting.",
  tools: [['notion', 'Notion'], ['slack', 'Slack']]
}, {
  category: 'people',
  name: 'Onboarding guide',
  blurb: 'Walks new starters through setup and answers from the handbook as they go.',
  tools: [['notion', 'Notion'], ['slack', 'Slack']]
}, {
  category: 'finance',
  name: 'Invoice chaser',
  blurb: 'Chases unpaid invoices on a schedule and logs every reply against the account.',
  tools: [['xero', 'Xero'], ['gmail', 'Gmail']]
}];

/** The chip. */
export function CategoryChip({
  category
}: {
  category: AgentCategory;
}) {
  const meta = CATEGORIES[category];
  const Icon = meta.icon;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold ${meta.className}`} data-id="element-190" data-mp="21agsx5eyu5h8">
      <Icon size={13} strokeWidth={2.4} data-id="element-191" data-mp="268s3xjgzym11" />
      {meta.label}
    </span>;
}

/**
 * ONE AGENT, as it appears in the directory. This is the page's atomic unit —
 * it is the hero overlay, it tiles into the template wall, and it is what the
 * sharing panel lists. Using the same object in all three places is what makes
 * the page read as one product rather than five illustrations.
 */
export function AgentCard({
  template,
  className = ''
}: {
  template: AgentTemplate;
  className?: string;
}) {
  return <div className={`rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${className}`} data-id="element-192" data-mp="pvjzzkfnuw4l">
      <CategoryChip category={template.category} data-id="element-193" data-mp="icaqlr75mxfj" />
      <p className="font-title mt-3 text-[17px] font-bold tracking-[-0.01em] text-[#2B211A] md:text-[18px]" data-id="element-194" data-mp="dl2j50pdxm50">
        {template.name}
      </p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-[#6B5D52] md:text-[13.5px]" data-id="element-195" data-mp="8mixz2im8edp">{template.blurb}</p>
      <div className="mt-4 flex items-center gap-2" data-id="element-196" data-mp="4lp9o819gxka">
        {template.tools.map(([slug, name]) => <span key={slug} className="flex size-8 items-center justify-center rounded-full border border-[#EFE7DA] bg-white" data-id="element-197" data-mp="1193pc2z3cqh9">
            <IntegrationLogo slug={slug} name={name} className="size-[17px]" rounded="rounded" data-id="element-198" data-mp="w97gnqfg8avd" />
          </span>)}
      </div>
    </div>;
}

/**
 * THE TEMPLATE WALL — a spotlight onto a larger directory.
 *
 * There are MORE of these than fit, and the artefact's whole job is to say so.
 * The earlier version said it with a hard crop inside a bordered, rounded
 * frame — but a frame ANSWERS the question: cards stop because the box stops,
 * so it reads as a picture of six cards rather than a window onto hundreds.
 *
 * So there is no frame. The grid is oversized and bled past its column on
 * BOTH sides, then a single radial mask dissolves it into the section's own
 * ground. Nothing terminates the artwork except light falling off, which is
 * what makes the eye assume the wall continues.
 *
 * Two deliberate details:
 *   · The lit region sits high and slightly left of centre, over the middle
 *     card. A centred spotlight reads as a vignette effect; an off-centre one
 *     reads as a camera pointed at something.
 *   · The card under that light is the only one at full weight. Its neighbours
 *     sit back, so a dense grid has one thing to actually look at.
 *
 * The mask is a real mask rather than an overlay: these cards are not
 * interactive, and a scrim painted on top would have to match the ground
 * colour exactly or read as haze sitting over the page.
 */

/** Off-centre, high. Shared by the mask and the board so they fall off together. */
const WALL_LIGHT = 'radial-gradient(118% 95% at 46% 32%, #000 0%, #000 36%, rgba(0,0,0,0.74) 57%, rgba(0,0,0,0.3) 76%, transparent 90%)';
export function TemplateWall() {
  // Bled hard to the OUTER edge and only slightly inward: this artefact sits
  // in the right-hand half of its beat, so a symmetric bleed would push cards
  // under the copy on its left.
  // Two full rows, wide cards. Three narrow columns in a half-width column
  // made a portrait block; two wide ones over the same bled width give each
  // card room for its own type and land the artefact close to square.
  return <div aria-hidden="true" className="relative w-full select-none" style={{
    maskImage: WALL_LIGHT,
    WebkitMaskImage: WALL_LIGHT
  }} data-id="element-199" data-mp="2ltlfvngnnabl">
      {/* The board the directory is ruled on. No fill and no border — only the
       grid — so the light is the only thing defining where it ends. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.55]" style={{
      backgroundImage: 'linear-gradient(to right, #E7DCCB 1px, transparent 1px), linear-gradient(to bottom, #E7DCCB 1px, transparent 1px)',
      backgroundSize: '72px 72px'
    }} data-id="element-200" data-mp="2px9jxw62vns3" />

      {/* Four templates share one horizontal shelf. The parent gives this an
       intentionally wide canvas before reducing the complete object, so each
       card retains a readable internal scale instead of being squeezed into a
       narrow four-column grid. */}
      <div className="relative grid grid-cols-4 gap-5 px-4 py-8 md:gap-6 md:px-6 md:py-10" data-id="element-201" data-mp="3ijrgpk32svlr">
        {AGENT_TEMPLATES.slice(0, 4).map((template, index) => {
        // The second card sits beneath the off-centre spotlight.
        const lit = index === 1;
        return <div key={template.name} className="relative" data-id="element-202" data-mp="3sb5oef56l1pz" data-mpi={index}>
              <AgentCard template={template} className={lit ? 'shadow-[0_2px_4px_rgba(43,33,26,0.06),0_26px_60px_-24px_rgba(43,33,26,0.34)]' : 'opacity-[0.72] shadow-[0_1px_2px_rgba(43,33,26,0.04),0_12px_28px_-18px_rgba(43,33,26,0.16)]'} data-id="element-203" data-mp="2uuqbe5pji0kp" />
              {/* The pointer names WHICH card the claim is about. Sat on the
               lit card's lower-left corner, half on and half off it, so it
               reads as resting over the directory rather than as an icon
               placed inside a card. */}
              {lit && <PixelCursor className="pointer-events-none absolute -bottom-5 -left-4 h-11 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)] md:-bottom-6 md:-left-5 md:h-14" data-id="element-204" data-mp="2e8y5sb7r6nid" />}
            </div>;
      })}
      </div>
    </div>;
}

/** Integrations, as toggles. Two on, the rest waiting — the state a workspace
 * is actually in on day one, which is more honest than an all-green list. */
const CONNECTIONS: [string, string, boolean][] = [['slack', 'Slack', true], ['salesforce', 'Salesforce', true], ['notion', 'Notion', false], ['gmail', 'Gmail', false], ['jira', 'Jira', false]];
export function ConnectionsCard() {
  return <div className={`w-full max-w-[230px] rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${CARD_SHADOW}`} data-id="element-205" data-mp="1llu52ifp0vce">
      <p className="font-title text-[16px] font-bold tracking-[-0.02em] text-[#2B211A]" data-id="element-206" data-mp="1swzg7hsj5mvz">Connections</p>
      <ul className="mt-2.5" data-id="element-207" data-mp="1nxwkinyfwt0o">
        {CONNECTIONS.map(([slug, name, on]) => <li key={slug} className="flex items-center gap-2 border-b border-[#F1EAE0] py-2 last:border-0" data-id="element-208" data-mp="22nkdn1wgwldb">
            <span aria-hidden="true" className={`flex h-[18px] w-[31px] flex-none items-center rounded-full px-[3px] transition-colors ${on ? 'justify-end bg-[#EA580C]' : 'justify-start bg-[#E7E0D4]'}`} data-id="element-209" data-mp="1xqaolnczqbq0">
              <span className="flex size-3 items-center justify-center rounded-full bg-white" data-id="element-210" data-mp="152y2tfenm7xr">
                {on && <Check size={8} strokeWidth={3.5} className="text-[#EA580C]" data-id="element-211" data-mp="1jzp6i0vd25t6" />}
              </span>
            </span>
            <IntegrationLogo slug={slug} name={name} className="size-[18px]" rounded="rounded" data-id="element-212" data-mp="1f1nn1eu0w4h6" />
            <span className="text-[12.5px] font-semibold text-[#2B211A]" data-id="element-213" data-mp="2vfdsoq0sj0sx">{name}</span>
          </li>)}
      </ul>
    </div>;
}

/**
 * THE APPROVAL PROMPT. The most load-bearing artefact on the page: it is the
 * only one that shows the agent NOT acting. Three actions in ascending trust —
 * decline, approve once, approve always — because the middle one is the choice
 * most people make first and it should not be the loud one.
 *
 * THE ACTION HAS TO BE ONE WORTH STOPPING FOR. This used to ask permission to
 * post a Slack message, which quietly argued the opposite of its point: if the
 * gate fires on something that harmless, it reads as friction on every action
 * rather than as judgement about the few that matter. Raising a campaign's
 * daily budget spends real money and cannot be un-spent, so the pause is
 * obviously right — and the sheet shows the old value beside the new one,
 * because an approval you cannot check is a rubber stamp.
 */
export function ApprovalCard({
  /**
   * 'glass' lays the prompt ON a photograph: a frosted panel holding a solid
   * white data sheet. The frost is what makes it read as a system prompt
   * surfacing over what the person is doing, rather than a card sitting on a
   * picture — and the data sheet stays opaque because it is the part you have
   * to actually read before you press anything.
   */
  variant = 'card'
}: {
  variant?: 'card' | 'glass';
} = {}) {
  const glass = variant === 'glass';
  return <div className={glass ? 'w-full rounded-[16px] border border-white/45 bg-white/45 p-3.5 shadow-[0_1px_2px_rgba(43,33,26,0.06),0_26px_60px_-22px_rgba(43,33,26,0.4)] backdrop-blur-2xl' : `w-full max-w-[300px] rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${CARD_SHADOW}`} data-id="element-214" data-mp="442yg9re5mmt">
      <div className="flex items-start gap-3" data-id="element-215" data-mp="3mi2h97bfy699">
        <span className={`flex size-9 flex-none items-center justify-center rounded-xl bg-white ${glass ? 'shadow-[0_1px_2px_rgba(43,33,26,0.12)]' : 'border border-[#EFE7DA]'}`} data-id="element-216" data-mp="3mgrdh7hzwyi5">
          <IntegrationLogo slug="googleads" name="Google Ads" className="size-[18px]" rounded="rounded" data-id="element-217" data-mp="3ck67aiu8phcq" />
        </span>
        <p className="text-[13px] font-semibold leading-snug text-[#2B211A]" data-id="element-218" data-mp="37no0zej9haya">
          The campaign agent wants to raise a daily budget
        </p>
      </div>

      <dl className={`mt-3 text-[11.5px] ${glass ? 'rounded-xl bg-white p-3' : 'rounded-xl bg-[#FAF8F4] p-3'}`} data-id="element-219" data-mp="32ffz3v88th02">
        <div className="flex gap-4" data-id="element-220" data-mp="37gdek01vbwxv">
          <dt className="w-[70px] flex-none font-semibold text-[#8A7B6D]" data-id="element-221" data-mp="3cex1o3s77ert">Campaign</dt>
          <dd className="text-[#2B211A]" data-id="element-222" data-mp="3cckos51arulm">Q4 Enterprise · Search</dd>
        </div>
        <div className="mt-3 flex gap-4" data-id="element-223" data-mp="2eszpeo4ew5ob">
          <dt className="w-[70px] flex-none font-semibold text-[#8A7B6D]" data-id="element-224" data-mp="2jpzuavpiz2du">Daily cap</dt>
          {/* The old figure struck through beside the new one: the number is
           the decision, so it has to be checkable at a glance. */}
          <dd className="flex items-baseline gap-1.5 text-[#2B211A]" data-id="element-225" data-mp="2tgulmehmos65">
            <span className="text-[#8A7B6D] line-through" data-id="element-226" data-mp="1w16zj2kn30kt">$400</span>
            <span className="font-bold" data-id="element-227" data-mp="1vyugem35ak66">$1,200</span>
          </dd>
        </div>
        <div className="mt-3 flex gap-4" data-id="element-228" data-mp="20xxetnomds56">
          <dt className="w-[70px] flex-none font-semibold text-[#8A7B6D]" data-id="element-229" data-mp="6j0af56vz90x">Why</dt>
          <dd className="leading-relaxed text-[#2B211A]" data-id="element-230" data-mp="1kgo33odrv81">
            Cost per demo fell to $84 and the campaign has capped out four days running.
          </dd>
        </div>
      </dl>

      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-id="element-231" data-mp="gb60uahhpie0">
        <button type="button" className={`whitespace-nowrap rounded-full border border-[#E4B9A2] px-3 py-1.5 text-[10.5px] font-bold text-[#B4470F] transition-colors ${glass ? 'bg-white/70 hover:bg-white' : 'hover:bg-[#FDF0E6]'}`} data-id="element-232" data-mp="bhusu60xyvki">
          Decline
        </button>
        <span className="grow" data-id="element-233" data-mp="z8qoxfcfkcxk" />
        <button type="button" className={`whitespace-nowrap rounded-full px-3 py-1.5 text-[10.5px] font-bold text-[#2B211A] transition-colors ${glass ? 'bg-white hover:bg-white/90' : 'border border-[#E7E0D4] hover:bg-[#FAF8F4]'}`} data-id="element-234" data-mp="z6w1nudos8qb">
          Always allow
        </button>
        <button type="button" className="whitespace-nowrap rounded-full bg-[#EA580C] px-3.5 py-1.5 text-[10.5px] font-bold text-white transition-colors hover:bg-[#D14E0A]" data-id="element-235" data-mp="2w1afyav78rsx">
          Approve
        </button>
      </div>
    </div>;
}

/** The shared directory — the same agents, now as a team's list. */
const SHARED = [{
  icon: TrendingUp,
  name: 'Renewal brief agent'
}, {
  icon: BarChart3,
  name: 'Monday recap agent'
}, {
  icon: Megaphone,
  name: 'Campaign recap agent'
}, {
  icon: Headphones,
  name: 'Escalation watcher'
}, {
  icon: Users,
  name: 'Onboarding guide'
}, {
  icon: Sparkles,
  name: 'Custom agent builder'
}];
/** The list runs off the bottom of its sheet rather than ending — the sheet is
 * a view into a directory of 24, not a card containing six. */
const SHARED_FALL = 'linear-gradient(to bottom, #000 0%, #000 76%, rgba(0,0,0,0.35) 92%, transparent 100%)';
export function SharedAgentsCard() {
  return <div className="relative pl-4 pt-4 sm:pl-8 sm:pt-6" data-id="element-236" data-mp="2d8boamdlun74">
      {/* Two sheets behind, offset up and to the LEFT and very slightly turned,
       so the front panel reads as the top of a stack somebody is holding.
       Offset and rotated rather than scaled — a scaled copy reads as a shadow. */}
      <div aria-hidden="true" className="absolute left-0 top-0 hidden h-[86%] w-[92%] rounded-[26px] bg-[#F2EBE1] sm:block" data-id="element-237" data-mp="2bp1zi8t9sxg9" />
      <div aria-hidden="true" className="absolute left-3 top-2.5 hidden h-[92%] w-[94%] -rotate-[1.4deg] rounded-[26px] border border-[#EFE7DA] bg-[#FBF6EF] shadow-[0_18px_44px_-28px_rgba(43,33,26,0.3)] sm:block" data-id="element-238" data-mp="26td7qrnhskui" />

      <div className="relative" data-id="element-239" data-mp="2uh4hbnhnejs3">
        {/* The tab bar is its OWN floating sheet, not a header inside the
         panel. That separation is what makes the two objects read as chrome
         over content rather than one long card. */}
        <div className={`relative z-10 flex items-center gap-7 rounded-[18px] border border-[#EFE7DA] bg-white px-5 py-3.5 ${CARD_SHADOW}`} data-id="element-240" data-mp="2lnrkwbkdl019">
          <span className="relative pb-2.5 text-[16px] font-bold text-[#EA580C]" data-id="element-241" data-mp="3i8jpvy00pxbs">
            Shared with you
            <span className="ml-2.5 inline-flex size-7 items-center justify-center rounded-full bg-[#EA580C] align-middle text-[12.5px] font-bold text-white" data-id="element-242" data-mp="zxcf1mhqx7x2">
              12
            </span>
            {/* The underline runs past the label to the sheet's edge, as in a
             real tab strip. */}
            <span aria-hidden="true" className="absolute -bottom-[3px] left-0 right-0 h-[3px] rounded-full bg-[#EA580C]" data-id="element-243" data-mp="1dwvm0bytmd7s" />
            {/* The pointer sits on the tab that is selected, which is the one
             fact this artefact exists to state: what you are looking at is
             the subset shared WITH you, not the whole library. */}
            <PixelCursor className="pointer-events-none absolute -bottom-7 right-1 h-11 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)] md:h-12" data-id="element-244" data-mp="1snb77qr8ll5y" />
          </span>
          <span className="pb-2.5 text-[16px] font-semibold text-[#8A7B6D]" data-id="element-245" data-mp="21n81q90b5e8x">
            All
            <span className="ml-2.5 inline-flex size-7 items-center justify-center rounded-full bg-[#F1EAE0] align-middle text-[12.5px] font-bold text-[#6B5D52]" data-id="element-246" data-mp="26l9l6v1nbfkx">
              24
            </span>
          </span>
        </div>

        {/* The content sheet, tucked under the tab bar. */}
        <div className={`relative -mt-3 overflow-hidden rounded-[22px] border border-[#EFE7DA] bg-white pt-7 ${CARD_SHADOW}`} data-id="element-247" data-mp="1qj23tro2cd0v">
          <p className="font-title px-6 text-[27px] font-bold tracking-[-0.02em] text-[#2B211A]" data-id="element-248" data-mp="1mirymh83hqn2">
            Agent
          </p>

          <ul className="mt-2 px-6 pb-2" style={{
          maskImage: SHARED_FALL,
          WebkitMaskImage: SHARED_FALL
        }} data-id="element-249" data-mp="yse4ml5y9hpu">
            {SHARED.map(({
            icon: Icon,
            name
          }) => <li key={name} className="flex items-center gap-4 border-b border-[#F5EFE6] py-3.5 last:border-0" data-id="element-250" data-mp="17mkf7j4igt8u">
                <span aria-hidden="true" className="flex size-10 flex-none items-center justify-center rounded-full bg-[#FAF8F4] text-[#8A7B6D]" data-id="element-251" data-mp="12rwnlbe7niq1">
                  <Icon size={17} strokeWidth={2.2} data-id="element-252" data-mp="b1ihw2gcpcpg" />
                </span>
                {/* The shared glyph sits DIRECTLY after the name, not pinned to
                 the far right. It qualifies the name, and a right-aligned
                 column of identical glyphs would read as a table column
                 carrying no information. */}
                <span className="flex min-w-0 items-center gap-2.5" data-id="element-253" data-mp="3n40b6625kz3j">
                  <span className="truncate text-[16.5px] font-semibold text-[#2B211A]" data-id="element-254" data-mp="3s5fthqcy4ri8">{name}</span>
                  <Users size={15} className="flex-none text-[#B6A895]" data-id="element-255" data-mp="jpnwpq21lg4n" />
                </span>
              </li>)}
          </ul>
        </div>
      </div>
    </div>;
}

/**
 * THE BUILDER — two overlapping sheets. The editor states what the agent is
 * for; the settings sheet in front of it states what it is allowed to use. Two
 * objects rather than one tall form, because the overlap is what says these
 * are panels of a live tool.
 */
export function AgentBuilderCard() {
  return <div className="relative pb-7 pr-5 sm:pr-10" data-id="element-256" data-mp="2zhbrbetl2wka">
      <div className={`w-full max-w-[275px] rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${CARD_SHADOW}`} data-id="element-257" data-mp="1zbizdaq0929p">
        <div className="flex items-center gap-3" data-id="element-258" data-mp="24adf23soeapl">
          <span aria-hidden="true" className="flex size-9 flex-none items-center justify-center rounded-xl bg-[#FDF0E6] text-[#EA580C]" data-id="element-259" data-mp="24a3qmkna4bm0">
            <TrendingUp size={16} strokeWidth={2.3} data-id="element-260" data-mp="1v9xhfaym5xsn" />
          </span>
          <p className="font-title text-[16px] font-bold leading-tight tracking-[-0.02em] text-[#2B211A]" data-id="element-261" data-mp="1v8497r8cr5ij">
            Renewal brief agent
          </p>
        </div>

        <p className="mt-4 text-[11px] font-semibold text-[#8A7B6D]" data-id="element-262" data-mp="1lcsz5m7w2mh4">Instructions</p>
        <p className="mt-1.5 rounded-xl bg-[#FAF8F4] p-3 text-[11.5px] leading-relaxed text-[#2B211A]" data-id="element-263" data-mp="17l5sqtk8fxkm">
          Before every renewal call, pull the account's history, the open threads and the last
          three meetings, and write a one-page brief.
        </p>

        {/* The pointer lands on the one control that commits the whole sheet.
         Everything above it is description; this is the moment the agent
         exists, which is what "in minutes" beside it is claiming. */}
        <div className="relative mt-5" data-id="element-264" data-mp="e0wsxy414dee">
          <button type="button" className="w-full rounded-full bg-[#EA580C] py-2.5 text-[12px] font-bold text-white transition-colors hover:bg-[#D14E0A]" data-id="element-265" data-mp="e1y74bgcdg0c">
            Create agent
          </button>
          <PixelCursor className="pointer-events-none absolute -bottom-4 left-[38%] h-8 w-auto drop-shadow-[0_6px_10px_rgba(43,33,26,0.3)]" data-id="element-266" data-mp="nwzxfamcso4i" />
        </div>
      </div>

      {/* In front and to the right, overlapping by about a third. */}
      <div className={`absolute bottom-0 right-0 w-[145px] rounded-[16px] border border-[#EFE7DA] bg-white p-3.5 ${CARD_SHADOW}`} data-id="element-267" data-mp="1bo5koaumiiou">
        <p className="text-[11px] font-semibold text-[#8A7B6D]" data-id="element-268" data-mp="16osrl8d5sfdt">Model</p>
        <p className="mt-1.5 flex items-center gap-1.5 rounded-full border border-[#EFE7DA] px-2.5 py-1 text-[10.5px] font-semibold text-[#2B211A]" data-id="element-269" data-mp="1kok93j2vwdwk">
          <Cpu size={12} className="text-[#8A7B6D]" data-id="element-270" data-mp="1gotbavexwxij" />
          Your choice
        </p>

        <p className="mt-3 text-[11px] font-semibold text-[#8A7B6D]" data-id="element-271" data-mp="1nrrg1q25k4en">Runs</p>
        <p className="mt-1.5 flex items-center gap-1.5 text-[10.5px] font-semibold text-[#2B211A]" data-id="element-272" data-mp="1soq9e6dzs7bs">
          <MessageSquare size={12} className="text-[#8A7B6D]" data-id="element-273" data-mp="1xocludlzxjjj" />
          On a Pulse
        </p>

        <p className="mt-3 text-[11px] font-semibold text-[#8A7B6D]" data-id="element-274" data-mp="22fkwfcz2baya">Connections</p>
        <div className="mt-1.5 flex items-center gap-1" data-id="element-275" data-mp="26h6f5eya2jge">
          {[['salesforce', 'Salesforce'], ['gmail', 'Gmail'], ['slack', 'Slack'], ['notion', 'Notion']].map(([slug, name]) => <span key={slug} className="flex size-6 items-center justify-center rounded-full border border-[#EFE7DA] bg-white" data-id="element-276" data-mp="2bhu660mib0am">
              <IntegrationLogo slug={slug} name={name} className="size-[13px]" rounded="rounded" data-id="element-277" data-mp="2leqhee3si0iv" />
            </span>)}
        </div>
      </div>
    </div>;
}